import { ChangeEvent, FC, useCallback } from "react";
import {
  Box,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Tooltip,
} from "@mui/material";
import Typography from "@mui/material/Typography";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { BudgetPeriod, BudgetType } from "@/services/cloudchipr.api";
import { useAppSelector } from "@/store/hooks";
import { isCurrentBudgetEditingSelector } from "@/store/budgets/selectors/current-budget/data-selectors/isCurrentBudgetEditingSelector";

interface BudgetTypeToggleProps {
  type: BudgetType;
  period: BudgetPeriod;
  onChange(id: string): void;
  plannedAmounts: number[] | null;
  onPlannedAmountChange(value: (number | undefined)[] | null): void;
}

export const BudgetTypeToggle: FC<BudgetTypeToggleProps> = ({
  type,
  period,
  onChange,
  plannedAmounts,
  onPlannedAmountChange,
}) => {
  const isEdit = useAppSelector(isCurrentBudgetEditingSelector);

  const changeHandler = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onChange(event.target.value);

      if (!unsupportedBudgetPeriods.includes(period) && !plannedAmounts) {
        let initialAmounts = [];

        if (period === "monthly") {
          initialAmounts = Array(12).fill(undefined) as undefined[];
          onPlannedAmountChange(initialAmounts);
        }

        if (period === "quarterly") {
          initialAmounts = Array(4).fill(undefined) as undefined[];
          onPlannedAmountChange(initialAmounts);
        }
      }
    },
    [onChange, period, onPlannedAmountChange, plannedAmounts],
  );

  const isPlannedOptionDisabled = unsupportedBudgetPeriods.includes(period);

  return (
    <Box>
      <Typography variant="body2">Budgeting Method</Typography>
      <RadioGroup
        row
        value={type}
        name="type"
        onChange={changeHandler}
        sx={{ p: 1 }}
      >
        <FormControlLabel
          value="fixed"
          control={<Radio size="small" sx={{ p: 0.5 }} disabled={isEdit} />}
          label={
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography
                variant="body2"
                color={isEdit ? "text.disabled" : "text.primary"}
              >
                Fixed
              </Typography>
              <Tooltip
                title={"Set one fixed amount to monitor every budget period."}
                placement="top"
                arrow
              >
                <InfoOutlinedIcon color="action" sx={{ fontSize: 16 }} />
              </Tooltip>
            </Stack>
          }
          sx={formControlLabelStyles}
        />

        <FormControlLabel
          value="planned"
          control={
            <Radio
              size="small"
              sx={{
                p: 0.5,
                cursor:
                  isEdit || isPlannedOptionDisabled ? "default" : "pointer",
              }}
              disabled={isPlannedOptionDisabled || isEdit}
            />
          }
          label={
            <Stack direction="row" alignItems="center" spacing={2}>
              <Tooltip
                title={
                  isPlannedOptionDisabled && !isEdit
                    ? "Planned budgeting method is available only for budgets with Monthly or Quarterly periods."
                    : ""
                }
                placement="top"
                arrow
              >
                <span>
                  <Typography
                    variant="body2"
                    color={
                      isPlannedOptionDisabled || isEdit
                        ? "text.disabled"
                        : "text.primary"
                    }
                  >
                    Planned
                  </Typography>
                </span>
              </Tooltip>
              <Tooltip
                title={"Set different amounts to monitor each budget period."}
                placement="top"
                arrow
              >
                <InfoOutlinedIcon color="action" sx={{ fontSize: 16 }} />
              </Tooltip>
            </Stack>
          }
          sx={formControlLabelStyles}
        />
      </RadioGroup>
    </Box>
  );
};

const formControlLabelStyles = {
  p: 1,
  border: 1,
  borderColor: "divider",
  borderRadius: 1,
};

const unsupportedBudgetPeriods = ["daily", "annually", "custom"];
